import { updateStructureWasSaved } from "@src/reducers/your-reducer";
import store from "@src/reducers/store";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { i18n } from "@src/i18n/i18n";
import { transferDashboards } from "./transfer-dashboards";
import { transferAlarms } from "./transfer-alarms";
import { buildPayloadAndTransferConfigurationUseCase } from "./build-payload-and-transfer-configuration";
import { getLogger } from "@src/cloud-connect/dependency-injection-container/get-logger";
import { getTileCollection } from "@src/cloud-connect/dependency-injection-container/get-tile-collection";
import { writeReduxStateForMachineStructureWithLatestSavedStructure } from "./write-redux-state-for-machine-structure-with-latest-saved-structure";

// this could be a use case

export const transferNodeStructure = async () => {
  try {
    const data = await buildPayloadAndTransferConfigurationUseCase();

    if (
      data !== undefined &&
      data.statusCode === 422 &&
      data.message.includes("50000")
    ) {
      return {
        success: false,
        error: new Error(
          "Unprocessable Entity: nodeset size exceeds maximum number of nodes (50000)",
        ),
        message:
          "Unable to update nodeset: nodeset size exceeds maximum number of nodes (50000)",
      };
    } else if (data === undefined || data === null || data.statusCode === 422) {
      // If data is undefined or null, treat as failure
      // because saveStructureFunction swallows errors (reroutes and that's it)
      return {
        success: false,
        error: new Error("No data returned from saveStructureFunction"),
        message: "Failed to transfer node structure",
      };
    } else {
      return { success: true, data, message: "Node structure transferred" };
    }
  } catch (error) {
    // Just in case some error would come back
    return {
      success: false,
      error,
      message: "Failed to transfer node structure",
    };
  }
};

export const transferConfigurationToClient = async (
  overwriteReduxStateWithLastSavedStructure: boolean,
  setIsButtonLoading?: (loading: boolean) => void,
) => {
  const logger = getLogger();
  const dashboardData = getTileCollection();
  const dashboardDataTiles = dashboardData.getTiles();
  const dashboardId = dashboardData.getDashboardId();

  const language = store.getState().yourSlice.language;

  const texts = {
    success: i18n("modules.newTransferConfigurationToClient.success", language),
    error: i18n("modules.newTransferConfigurationToClient.error", language),
    errorNodeStructure: i18n(
      "modules.newTransferConfigurationToClient.errorNodeStructure",
      language,
    ),
    errorNodeStructureTooManyNodes: i18n(
      "modules.newTransferConfigurationToClient.errorNodeStructureTooManyNodes",
      language,
    ),
    errorDashboards: i18n(
      "modules.newTransferConfigurationToClient.errorDashboards",
      language,
    ),
    errorAlarms: i18n(
      "modules.newTransferConfigurationToClient.errorAlarms",
      language,
    ),
  };

  setIsButtonLoading && setIsButtonLoading(true);
  const nodeResult = await transferNodeStructure();

  let dashboardResult;
  let alarmResult;

  if (nodeResult && nodeResult.success) {
    [dashboardResult, alarmResult] = await Promise.all([
      transferDashboards(dashboardId, dashboardDataTiles),
      transferAlarms(),
    ]);
  }

  logger.debug("Transfer Results:", {
    nodeResult,
    dashboardResult: dashboardResult || "No dashboard result",
    alarmResult: alarmResult || "No alarm result",
  });

  if (nodeResult.success && overwriteReduxStateWithLastSavedStructure) {
    // when saving, if the node structure gets saved successfully
    // we overwrite the root node in the machine structure nodeset stored
    // in redux so that the latest status is available next time a change is discarded
    // but only if the user is not exiting the node manager
    writeReduxStateForMachineStructureWithLatestSavedStructure();
  }

  // TODO: maybe look into this and rewrite the comment so that it makes more sense
  // set the dashboardId in case the user wants to put it right away, without refreshing the page

  if (
    dashboardResult &&
    dashboardResult.success &&
    dashboardResult.data &&
    dashboardResult.data.id
  ) {
    getTileCollection().setDashboardId(dashboardResult.data.id);
  }

  const failedParts = [];

  if (!nodeResult.success && !nodeResult.message.includes("50000")) {
    failedParts.push(texts.errorNodeStructure);
  }

  if (!nodeResult.success && nodeResult.message.includes("50000")) {
    failedParts.push(texts.errorNodeStructureTooManyNodes);
  }

  if (dashboardResult && !dashboardResult.success) {
    failedParts.push(texts.errorDashboards);
  }

  if (alarmResult && !alarmResult.success) {
    failedParts.push(texts.errorAlarms);
  }

  if (failedParts.length === 0) {
    successSnackbar(texts.success);
    store.dispatch(updateStructureWasSaved(true));
  } else if (failedParts.length === 1 && nodeResult.message.includes("50000")) {
    // Special case for too many nodes
    errorSnackbar(failedParts[0], 5000);
    logger.debug("Transfer error: Too many nodes in node structure");
  } else {
    const failureMessage = `${texts.error} ${failedParts.join(", ")}`;
    errorSnackbar(failureMessage, 5000);

    logger.debug("Transfer errors:", {
      node: nodeResult.error,
      dashboard:
        (dashboardResult && dashboardResult.error) || "No dashboard error",
      alarm: (alarmResult && alarmResult.error) || "No alarm error",
    });
  }
  setIsButtonLoading && setIsButtonLoading(false);
};
